import React, {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import SMCTableBody from './SMCTableBody';
import SMCTableHeader from './SMCTableHeader';
import { makeStyles } from '@mui/styles';
import union from 'lodash/union';
import {DragDropContext} from 'react-beautiful-dnd';
import AppTheme from '../../../conf/AppTheme';
import RowLayout from '../../Layouts/RowLayout';
import SimpleBar from 'simplebar-react';
import {SortDirections} from '../../../api/consts';
import {hasValue} from '../../../utils/utils';

/**
 * @typedef  {Object} Column
 * @property {string} id - Row id - required and must be unique
 * @property {string} [accessor] - Accessor to column value from content
 * @property {string} [header] - Table column header
 * @property {number} [width] - Column width
 * @property {function(object, object)} [sort] - Function that receives two objects and returns 0 if equal, -1 if the first is ahead, 1 is the second is ahead, by that column
 * @property {'left'|'right'|'center'|'justify'} [align] - Align the header and cell content
 * @property {Node} [Cell]
 */

/**
 * 
 * @param {Object} params
 * @param {[Column]} params.columns - Table columns
 * @param {Object} [params.content] - The data with which the table builds its rows
 * @param {[string]} [params.columnsState] - List of columns to show, and the order to show them, if not set, all columns are shown with the original order
 * @param {function} [params.setColumnsState] - Set the columnsState list, if not set, options to set columnsState will be hidden
 * @param {function} [params.setDefaultColumnsState] - Function to reset columnsState back to default
 * @param {[Object]} [params.defaultColumnsWidths] - Default widths of columns, if null or undefined, widths will be taken from **columns**
 * @param {function} [params.onEndResizingColumns] - Function triggered when columns resizing is finished, after the user releases the drag, or on reset widths to default
 * @param {[string]} [params.selectedObjects] - Currently selected objects
 * @param {function} [params.setSelectedObjects] - Set selected objects callback
 * @param {boolean} [params.isFilterOpen] - Is the filter sidebar open, only relavant if `filterElem` is set
 * @param {Node} [params.filterElem] - Element to open as sidebar when `isFilterOpen` is true
 * @param {boolean} [params.hasHeader] - If table has header
 * @param {boolean} [params.hasCheckAll] - If table has a check all checkbox (sits in header)
 * @param {boolean} [params.showSelectedBox] - Does the table have a "selected box" beneath it
 * @param {number} [params.selectedBoxHeight] - Height of "selected box"
 * @param {boolean} [params.isRowsDraggable] - Rules if table rows can be dragged and dropped in table
 * @param {function} [params.onDragEnd] - Event for when drag of row ends
 * @param {boolean} [params.loading] - Display loading indicator
 * @param {function} [params.setSort] - Callback function for when sort is set
 * @param {string} [params.sortBasis] - Current sort basis, if any
 * @param {SortDirections} [params.sortDirection] - Current sort direction, if any
 * @param {function} [params.onRequestLoadPage] - Function that is called when the table wants to load the next page
 * @param {string|number} [params.width] - Outer width of the table, including the filter pane
 * @param {string|number} [params.height] - Outer width of the table, including the selection box
 * @param {boolean} [params.isLastPage] - Is last page of the table
 * @param {boolean|function(object, number)} [params.isRowSelectable] - Function that recieves a row object, and index, and returns if the row is selectable or not, or boolean for set all
 * @param {function(object, number)} [params.rowIdSelector] - Function that recieves a row object, and index, and returns the id of the row to use for selection
 * @param {boolean} [params.allowRowWrap] - Is row wrap allowed, if false, cell overflow will cause the content to be cut
 * @param {boolean} [params.hasOuterBorder] - Does table have outer border
 */
function SMCTable({
    columns,
    content,
    columnsState,
    setColumnsState,
    setDefaultColumnsState,
    defaultColumnsWidths,
    onEndResizingColumns,
    selectedObjects,
    setSelectedObjects,
    isFilterOpen=false,
    filterElem,
    textToHighlight,
    showSelectedBox=false,
    selectedBoxHeight=52,
    selectedBoxNameSelector,
    hasHeader=true,
    hasCheckAll=false,
    isDraggable=false,
    onDragEnd,
    loading=false,
    setSort,
    sortBasis,
    sortDirection,
    onRequestLoadPage,
    width,
    height,
    maxHeight=800,
    isLastPage=true,
    hasCheckboxColumn=true,
    isRowSelectable,
    isColumnsResizable=true,
    rowIdSelector,
    onClickRow,
    allowRowWrap=false,
    hasOuterBorder=true,
    showEditIcon=true
}) {
    const classes = useStyles({hasOuterBorder});

    const headerRef = useRef();
    const [verticalScrollbarWidth, setVerticalScrollbarWidth] = useState(0);
    const [horizontalScrollbarHeight, setHorizontalScrollbarHeight] = useState(0);
    const [columnsWidths, setColumnsWidths] = useState([]);

    const headerHeight = useMemo(() => {
        if (!hasHeader) {
            return 0;
        }
        return AppTheme.tableHeaderHeight;
    }, [hasHeader]);

    // Check if content needs to be sorted, and sort if needed, using sort method of column
    const sortedContent = useMemo(() => {
        if (!content || !content.length || !sortBasis) {
            return content;
        }
        // Find column requested to sort, if not found, or doesn't have sort method, don't sort content
        const columnToSort = columns.find(col => col.id === sortBasis);
        if (!columnToSort || !columnToSort['sort']) {
            return content;
        }

        let sortMethod = columnToSort['sort'];
        // If direction is descending, reverse sort return value
        if (sortDirection === SortDirections.DESCENDING) {
            sortMethod = (a, b) => (columnToSort['sort'](a,b) * -1);
        }
        const _sortedContent = content.sort(sortMethod);

        return _sortedContent;
    }, [columns, content, sortBasis, sortDirection]);

    const setDefaultWidths = useCallback(() => {
        const defaultColumnsWidths = columns.map(col => ({ id: col.id, width: col.width}));
        setColumnsWidths(defaultColumnsWidths);
        onEndResizingColumns && onEndResizingColumns(defaultColumnsWidths);
    }, [columns, onEndResizingColumns]);

    const tableContainerWidth = useMemo(() => {
        let computedWidth = undefined;

        // If width is given, use the given width, otherwise calculate the table's dynamic width
        if (width) {
            computedWidth = width;
        } else if (columns) {
            const columnsWidthSum = columns.map(elem => elem.width).reduce((acc, cur) => acc + cur);
            const separatorsWidthSum = (columns.length-1) * 3;
            computedWidth = columnsWidthSum + separatorsWidthSum + 32;
        }
        return computedWidth;
    }, [columns, width]);

    const tableWidth = useMemo(() => {
        let computedWidth = tableContainerWidth;
        
        if (isFilterOpen && filterElem) {
            computedWidth -= (AppTheme.filterPageWidth + 10);
        }
        return computedWidth;
    }, [tableContainerWidth, isFilterOpen, filterElem]);

    const tableBodyHeight = useMemo(() => {
        let computedHeight;

        // If entered height, take this height
        if (height) {
            computedHeight = height;
        }
        // If not entered height, but there is content, calculate height dynamically
        else if (content && content.length) {
            const columnsHeightSum = content.length * AppTheme.tableRowHeight;
            computedHeight = Math.min(columnsHeightSum, maxHeight);
            if (horizontalScrollbarHeight) {
                computedHeight += horizontalScrollbarHeight;
            }    
        }
        else {
            computedHeight = undefined;
        }

        if (computedHeight && showSelectedBox) {
            computedHeight -= selectedBoxHeight;
        }

        return computedHeight;
    }, [content, height, maxHeight, showSelectedBox, selectedBoxHeight, horizontalScrollbarHeight]);


    const filterHeight = useMemo(() => {
        return height + headerHeight + 2;
    }, [height, headerHeight]);

    /**
     * Are all objects selected
     * 1. Are there any objects?
     * 2. Are all content row-ids included in selectedObjects
     */
    const isAllChecked = useMemo(() => {
        // Don't calculate this if there is no check all, this is a heavy operation
        if (!hasCheckAll) {
            return undefined;
        }
        // No row is selectable
        if (isRowSelectable === false) {
            return false;
        }
        if (rowIdSelector && content && content.length && selectedObjects && selectedObjects.length) {
            for (let index = 0; index < content.length; index++) {
                // If row is not selectable, don't check this row
                if (typeof isRowSelectable === 'function' && !isRowSelectable(content[index], index)) {
                    continue;
                }
                const rowId = rowIdSelector(content[index], index);
                if (!selectedObjects.includes(rowId)) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }, [hasCheckAll, content, selectedObjects, rowIdSelector, isRowSelectable]);

    const checkAllOnChange = useCallback(() => {
        // No row is selectable
        if (isRowSelectable === false) {
            return;
        }
        if (content && content.length) {
            if (isAllChecked) {
                const checkableRows = typeof isRowSelectable === 'function' ? content.filter(isRowSelectable) : content;
                const allRowIds = checkableRows.map(rowIdSelector);
                setSelectedObjects(selectedObjects.filter(sel => !allRowIds.includes(sel)));
            }
            else {
                const checkableRows = typeof isRowSelectable === 'function' ? content.filter(isRowSelectable) : content;
                setSelectedObjects(union(selectedObjects, checkableRows.map(rowIdSelector).filter(rowId => hasValue(rowId))));
            }
        }
    }, [isAllChecked, content, rowIdSelector, selectedObjects, setSelectedObjects, isRowSelectable]);

    const onScrollBody = useCallback((scrollX) => {
        if (hasHeader) {
            headerRef.current.scrollLeft = scrollX;
        }
    }, [hasHeader]);

    // Calculate scrollbar sizes to adjust table sizes to them
    const onBodyResize = useCallback((bodyDivDomObject) => {
        if (bodyDivDomObject) {
            const verticalScrollbarWidth = tableWidth - bodyDivDomObject.clientWidth;
            let horizontalScrollbarHeight = bodyDivDomObject.offsetHeight - bodyDivDomObject.clientHeight;
            // If there is a scrollbar, add one pixel to its height, because sometimes
            // the browser sends us a smaller height then its real height (rounded down)
            if (horizontalScrollbarHeight) {
                horizontalScrollbarHeight += 1;
            }
            setHorizontalScrollbarHeight(horizontalScrollbarHeight);
            setVerticalScrollbarWidth(verticalScrollbarWidth);
        }
    }, [tableWidth]);

    const handleEndResizingColumns = useCallback(columnsWidths => {
        onEndResizingColumns && onEndResizingColumns(columnsWidths);
    }, [onEndResizingColumns]);

    // Values to show at selected box section
    // SelectedBoxValues format is { [id]: <the selected id>, [name]: <name shown in the chip> }
    const [selectedBoxValues, setSelectedBoxValues] = useState([]);

    // Set selected box values
    useEffect(() => {
        if (showSelectedBox) {
            if (selectedBoxNameSelector) {
                if (selectedObjects && selectedObjects.length) {
                    const rowIdToSelectBoxName = {};
                    content.forEach((rowContent, index) => {
                        const rowId = rowIdSelector ? rowIdSelector(rowContent, index) : index;
                        const selectedBoxName = selectedBoxNameSelector(rowContent, index);
                        rowIdToSelectBoxName[rowId] = selectedBoxName;
                    });
                    setSelectedBoxValues(selectedObjects.map(rowId => ({ id: rowId, name: rowIdToSelectBoxName[rowId] })));
                } else {
                    setSelectedBoxValues([]); // None selected
                }
            } else {
                setSelectedBoxValues(selectedObjects.map(rowId => ({ id: rowId, name: rowId }))); // No selectedBoxNameSelector, set name as row id
            }
        }
    }, [showSelectedBox, selectedBoxNameSelector, selectedObjects, content, rowIdSelector]);

    // Extract columns widths on setting of columns
    useEffect(() => {
        // If columns are not resizable, no need to caculate this
        if (isColumnsResizable) {
            if (defaultColumnsWidths) {
                setColumnsWidths(defaultColumnsWidths);
            } else if (columns) {
                const columnsWidths = columns.map(col => ({ id: col.id, width: col.width}));
                setColumnsWidths(columnsWidths);
            }
        }
        else {
            setColumnsWidths([]);
        }
    }, [columns, defaultColumnsWidths, isColumnsResizable]);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <RowLayout wrap="nowrap">
                <div>
                    <div className={classes.tableRoot} style={{ width: tableWidth }}>
                        <div>
                            {
                                hasHeader && (
                                    <SMCTableHeader
                                        onEndResizingColumns={handleEndResizingColumns}
                                        outerWidth={tableWidth}
                                        innerWidth={tableWidth-verticalScrollbarWidth-1}
                                        height={headerHeight}
                                        tableFullHeight={height}
                                        columns={columns}
                                        hasCheckboxColumn={hasCheckboxColumn}
                                        // Don't show check all if there is no content
                                        hasCheckAll={hasCheckAll && !!content && !!content.length}
                                        isAllChecked={isAllChecked}
                                        checkAllOnChange={checkAllOnChange}
                                        innerRef={headerRef}
                                        sortBasis={sortBasis}
                                        sortDirection={sortDirection}
                                        setSort={setSort}
                                        columnsState={columnsState}
                                        setColumnsState={setColumnsState}
                                        setDefaultColumnsState={setDefaultColumnsState}
                                        isColumnsResizable={isColumnsResizable}
                                        columnsWidths={columnsWidths}
                                        setColumnsWidths={setColumnsWidths}
                                        setDefaultWidths={setDefaultWidths}
                                    />
                                )
                            }
                            <SMCTableBody
                                width={tableWidth}
                                height={tableBodyHeight}
                                textToHighlight={textToHighlight}
                                isDraggable={isDraggable}
                                content={sortedContent}
                                selectedObjects={selectedObjects}
                                setSelectedObjects={setSelectedObjects}
                                columns={columns}
                                columnsWidths={columnsWidths}
                                onScroll={onScrollBody}
                                onResize={onBodyResize}
                                hasNextPage={!isLastPage}
                                loading={loading}
                                loadNextPage={onRequestLoadPage}
                                hasCheckboxColumn={hasCheckboxColumn}
                                isRowSelectable={isRowSelectable}
                                rowIdSelector={rowIdSelector}
                                columnsState={columnsState}
                                onClickRow={onClickRow}
                                allowRowWrap={allowRowWrap}
                                showEditIcon={showEditIcon}
                            />
                        </div>
                    </div>
                    {
                        showSelectedBox && (
                            <div className={classes.selectedBoxRoot} style={{ width: tableWidth, height: selectedBoxHeight }}>
                                {
                                    (selectedBoxValues && selectedBoxValues.length) ? (
                                        <>
                                        Selected Items ({selectedBoxValues.length}):<br />
                                        {
                                            selectedBoxValues.map(selectedObject => (
                                                <div className={classes.selectedBoxChip} key={selectedObject.name}>
                                                    {selectedObject.name}
                                                    <div className={classes.selectedBoxChipDelete} onClick={() => setSelectedObjects(selectedObjects.filter(item => item !== selectedObject.id))}>x</div>
                                                </div>
                                            ))
                                        }
                                        </>
                                    ) : (
                                        <>There are no selected items.</>
                                    )
                                }
                            </div>
                        )
                    }
                </div>
                <div>
                    {
                        // TODO: Make the filter open with a slide or other animation
                        filterElem &&
                        <SimpleBar className={classes.filterRoot} style={{ width: AppTheme.filterPageWidth, height: filterHeight, display: isFilterOpen ? undefined : 'none' }}>
                            {filterElem}
                        </SimpleBar>
                    }
                </div>
            </RowLayout>
        </DragDropContext>
    );
}

const useStyles = makeStyles({
    tableRoot: {
        backgroundColor: '#FFF',
        border: props => props.hasOuterBorder ? '1px solid #C8C9C9' : 'none',
        display: 'flex'
    },
    filterRoot: {
        border: '1px solid #E0E4E8',
        backgroundColor: '#FFF',
        overflowX: 'hidden',
        overflowY: 'auto',
        marginLeft: '10px'
    },
    selectedBoxRoot: {
        lineHeight: 'normal',
        marginTop: '4px',
        background: '#FFF',
        border: '1px solid #C8C9C9',
        boxSizing: 'border-box',
        overflow: 'hidden auto',
        padding: '4px'
    },
    selectedBoxChip: {
        display: 'inline-block',
        margin: '2px 2px 2px 0',
        padding:' 0 2px',
        whiteSpace: 'nowrap',
        borderRadius: '3px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#f3f7fe'
    },
    selectedBoxChipDelete: {
        display: 'inline',
        cursor: 'pointer',
        lineHeight: 1,
        fontWeight: 'bold',
        marginLeft: '2px'
    }
})

SMCTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    setDefaultColumnsState: PropTypes.func,
    setColumnsState: PropTypes.func,
    columnsState: PropTypes.arrayOf(PropTypes.object),
    defaultColumnsWidths: PropTypes.arrayOf(PropTypes.object),
    onEndResizingColumns: PropTypes.func,
    selectedObjects: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    setSelectedObjects: PropTypes.func,
    hasCheckboxColumn: PropTypes.bool,
    isFilterOpen: PropTypes.bool,
    filterElem: PropTypes.node,
    showSelectedBox: PropTypes.bool,
    selectedBoxHeight: PropTypes.number,
    hasCheckAll: PropTypes.bool,
    isDraggable: PropTypes.bool,
    onDragEnd: PropTypes.func,
    content: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    setSort: PropTypes.func,
    sortBasis: PropTypes.string,
    sortDirection: PropTypes.string,
    onRequestLoadPage: PropTypes.func,
    width: PropTypes.number,
    height: PropTypes.number,
    isRowSelectable: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    isLastPage: PropTypes.bool,
    allowRowWrap: PropTypes.bool,
    textToHighlight: PropTypes.string,
    selectedBoxNameSelector: PropTypes.func,
    hasHeader: PropTypes.bool,
    maxHeight: PropTypes.number,
    isColumnsResizable: PropTypes.bool,
    rowIdSelector: PropTypes.func,
    onClickRow: PropTypes.func,
    hasOuterBorder: PropTypes.bool
}

export default React.memo(SMCTable);